/**
 * @author luyinpeng
 * @date 2026-03-14
 * @description 公共方法
 */

/**
 * 递归处理对象中的字符串，自动去除首尾空格
 * @param obj 要处理的对象
 * @param processed 用于处理循环引用的WeakMap
 * @returns 处理后的对象
 */
function trimObjectStrings<T>(
    obj: T,
    processed = new WeakMap<object, unknown>(),
): T {
    // 1. 处理循环引用
    if (typeof obj === 'object' && obj !== null) {
        if (processed.has(obj)) {
            return processed.get(obj) as T;
        }
    }

    // 2. 处理字符串
    if (typeof obj === 'string') {
        return obj.trim() as T;
    }

    // 3. 处理数组
    if (Array.isArray(obj)) {
        const result = obj.map(item => trimObjectStrings(item, processed));
        processed.set(obj, result);
        return result as T;
    }

    // 4. 处理普通对象（排除特殊对象：Date、RegExp、Map、Set 等）
    if (typeof obj === 'object' && obj !== null) {
        // 跳过特殊对象类型，保留原样
        if (
            obj instanceof Date ||
            obj instanceof RegExp ||
            obj instanceof Map ||
            obj instanceof Set ||
            ArrayBuffer.isView(obj) // 处理 TypedArray（如 Uint8Array）
        ) {
            return obj;
        }

        // 创建新对象并保留原型链（如果是纯对象则用 Object.create(null) 也可）
        const result = Object.create(Object.getPrototypeOf(obj)) as Record<
            string,
            unknown
        >;
        processed.set(obj, result);

        // 遍历可枚举属性（用 Object.entries 替代 for...in + hasOwnProperty）
        for (const [key, value] of Object.entries(obj)) {
            result[key] = trimObjectStrings(value, processed);
        }

        return result as T;
    }

    // 5. 处理其他类型（number、boolean、undefined、null 等）
    return obj;
}
/**
 * 时间格式化 - 无视时区，直接提取字符串内的时间
 * 支持：2023-02-15T10:30:00Z | 2026-03-23T05:48:52.059+00:00 | 时间戳
 */
function formatDateTime(dateStr?: string | number | null): string {
    if (!dateStr) return '--';
    try {
        const input = String(dateStr).trim();

        // 处理时间戳
        if (!isNaN(Number(input))) {
            const d = new Date(Number(input));
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
        }

        // 标准正则：提取 YYYY MM DD HH mm ss
        const match = input.match(
            /^(\d{4})\D+(\d{1,2})\D+(\d{1,2})(?:\D+(\d{1,2}))?(?:\D+(\d{1,2}))?(?:\D+(\d{1,2}))?/,
        );

        if (!match) return '--';

        const [_, y, m, d, hh = '00', mm = '00', ss = '00'] = match;

        // 严格补零格式化
        return (
            [y, m.padStart(2, '0'), d.padStart(2, '0')].join('-') +
            ' ' +
            [
                hh.padStart(2, '0'),
                mm.padStart(2, '0'),
                ss.padStart(2, '0'),
            ].join(':')
        );
    } catch {
        return '--';
    }
}

/**
 * 去除小数末尾多余0，整数保留末尾0，最终返回数字
 * 增强输入校验：支持乱码、空字符串、空格、非数字等异常场景
 * @param val 字符串/数字/null/undefined
 * @returns 处理后纯数字，异常输入返回 0
 */
function trimEndZero(val: string | number | null | undefined): number {
    // 非法符号直接返回0，不返回原始字符避免NaN
    const invalidSymbols = ['-', '--', '_', '__'];
    if (invalidSymbols.includes(String(val).trim())) return 0;

    // 1. 空值判断
    if (val === null || val === undefined || val === '') return 0;

    // 2. 转字符串并去除首尾空格
    const str = String(val).trim();

    // 3. 判断是否为有效数字，无效直接返回 0
    if (isNaN(Number(str))) return 0;

    // 4. 区分整数 / 小数分别处理
    if (str.includes('.')) {
        // 带小数点：清除小数尾部0，再清除末尾小数点
        const clean = str.replace(/0+$/, '').replace(/\.$/, '');
        return Number(clean);
    } else {
        // 纯整数：直接原样转换，不删除末尾0
        return Number(str);
    }
}
export { trimObjectStrings, formatDateTime, trimEndZero };
